import { advanceSeat } from './dice-rules';
import { RoundIndex, Wind, type TRoundIndex, type TSeat, type TWind } from './types';

export const RoundOutcome = {
  DealerWin: 'dealer-win',
  NonDealerWin: 'non-dealer-win',
  Draw: 'draw',
} as const;

export type TRoundOutcome = (typeof RoundOutcome)[keyof typeof RoundOutcome];

export interface IGameSetProgress {
  readonly initialDealerSeat: TSeat;
  readonly dealerSeat: TSeat;
  readonly dealerStreak: number;
  readonly prevailingWind: TWind;
  readonly roundIndex: TRoundIndex;
  readonly completedRounds: number;
  readonly completedCircles: number;
}

export interface IGameSetTransition extends IGameSetProgress {
  readonly circleCompleted: boolean;
  readonly sessionCompleted: boolean;
}

const nextRoundIndex = (roundIndex: TRoundIndex): TRoundIndex =>
  (roundIndex === RoundIndex.North ? RoundIndex.East : roundIndex + 1) as TRoundIndex;

const nextPrevailingWind = (wind: TWind): TWind => {
  switch (wind) {
    case Wind.East:
      return Wind.South;
    case Wind.South:
      return Wind.West;
    case Wind.West:
      return Wind.North;
    case Wind.North:
      return Wind.North;
  }
};

export const transitionAfterRound = (
  progress: IGameSetProgress,
  outcome: TRoundOutcome
): IGameSetTransition => {
  const completedRounds = progress.completedRounds + 1;
  if (outcome === RoundOutcome.DealerWin || outcome === RoundOutcome.Draw) {
    return {
      ...progress,
      dealerStreak: progress.dealerStreak + 1,
      completedRounds,
      circleCompleted: false,
      sessionCompleted: false,
    };
  }

  const circleCompleted = progress.roundIndex === RoundIndex.North;
  const sessionCompleted = circleCompleted && progress.prevailingWind === Wind.North;

  return {
    ...progress,
    dealerSeat: circleCompleted ? progress.initialDealerSeat : advanceSeat(progress.dealerSeat, 1),
    dealerStreak: 0,
    prevailingWind:
      circleCompleted && !sessionCompleted
        ? nextPrevailingWind(progress.prevailingWind)
        : progress.prevailingWind,
    roundIndex: circleCompleted ? RoundIndex.East : nextRoundIndex(progress.roundIndex),
    completedRounds,
    completedCircles: progress.completedCircles + (circleCompleted ? 1 : 0),
    circleCompleted,
    sessionCompleted,
  };
};
